import { useDataQueryResult } from '@/plugin-system';
import { VisualSettingsProvider } from './hooks';
import { TimeSeriesChartPanelProcessing } from './TimeSeriesChartPanelProcessing';
import { TimeSeriesChartProps } from './time-series-chart-model';

export function TimeSeriesChartPanel(props: TimeSeriesChartProps) {
  const { width, height } = props;

  // TODO: consider refactoring how the layout/spacing/alignment are calculated
  // the next time significant changes are made to the time series panel (e.g.
  // when making improvements to the legend to more closely match designs).
  const contentDimensions = width && height ? { width, height } : undefined;

  const { queryResults, isLoading } = useDataQueryResult({ dataTypeFilter: 'TimeSeries' });

  // This should be a rare, temporary use case (e.g. if a dom node hasn't
  // loaded yet and therefore has no height/width). Covering it to avoid breakages
  // in these edge cases and for type safety.
  if (contentDimensions === undefined) {
    return null;
  }

  // Ensure results are loaded before moving on to additional panel processing.
  // Important since certain panel options, echarts features are optimized based on totalSeries or totalDatapoints.
  if (isLoading) {
    // Holds space while loading, so that when using a flexible layout strategy, the component changes height after
    // data loading when it knows how much space it needs, instead of the chart always starting small and growing
    // when data loads.
    return <div style={{ width, height }} />;
  }

  const hasData = queryResults.some((result) => result.data && result.data.series.length > 0);

  // At this point, we have a response from the backend for all queries. (We're past loading === true).
  // If we don't data from any of the queries, then check if we want to show an error.
  // Put another way: If any queries have data, even if other queries failed, we will show the data (not the error).
  // Unfortunately, partial errors get swallowed in this case.
  // This could be refactored when someone takes a look at validation and error-handling.
  if (!hasData) {
    for (const result of queryResults) {
      if (result.error) throw result.error;
    }
  }

  // Render the chart with data
  return (
    <VisualSettingsProvider visual={props.visual}>
      <TimeSeriesChartPanelProcessing {...props} width={contentDimensions.width} height={contentDimensions.height} />
    </VisualSettingsProvider>
  );
}
